import apiClient from '../../../api/client';
import type {
  PagoClienteResponse,
  PagoStatsResponse,
  PagoCoordenadasResponse,
  CreatePagoDto,
  QueryPagosDto,
  AnularPagoDto,
  BoletaOcrResult,
  PagoFifoPreviewRequest,
  PagoFifoPreviewResponse,
  PagoPrepagoPreviewRequest,
  PagoPrepagoPreviewResponse,
  Paginated,
  PrepagoResult,
  UsuarioPagoRef,
} from '@aid/shared';

// Re-export only the types consumers actually import from this module
// (knip i585: every other consumer reaches for @aid/shared directly).
export type {
  PagoClienteResponse,
  PagoStatsResponse,
  PagoCoordenadasResponse,
  CreatePagoDto,
  QueryPagosDto,
  AnularPagoDto,
  BoletaOcrResult,
  PagoFifoPreviewRequest,
  PagoFifoPreviewResponse,
  PagoPrepagoPreviewRequest,
  PagoPrepagoPreviewResponse,
  UsuarioPagoRef,
};

export type CreatePagoResponse = PagoClienteResponse | PrepagoResult;

export async function getPagos(params: QueryPagosDto): Promise<Paginated<PagoClienteResponse>> {
  const { data } = await apiClient.get<Paginated<PagoClienteResponse>>('/admin/pagos', { params });
  return data;
}

export async function getPagoCoordenadas(clienteId: number): Promise<PagoCoordenadasResponse> {
  const { data } = await apiClient.get<PagoCoordenadasResponse>(
    `/admin/clientes/${clienteId}/pagos/coordenadas`,
  );
  return data;
}

export async function getPagosStats(
  params: Omit<QueryPagosDto, 'offset' | 'limit'>,
): Promise<PagoStatsResponse> {
  const { data } = await apiClient.get<PagoStatsResponse>('/admin/pagos/stats', { params });
  return data;
}

export async function getPago(id: number): Promise<PagoClienteResponse> {
  const { data } = await apiClient.get<PagoClienteResponse>(`/admin/pagos/${id}`);
  return data;
}

/**
 * Users associated with at least one payment — the option list for the
 * "filter by user" control. Read-only and deliberately not cobrador-scoped:
 * filtering by an out-of-scope user simply yields an empty scoped result.
 */
export async function getPagosUsers(): Promise<UsuarioPagoRef[]> {
  const { data } = await apiClient.get<UsuarioPagoRef[]>('/admin/pagos/users');
  return data;
}

export async function createPago(dto: CreatePagoDto): Promise<CreatePagoResponse> {
  const { data } = await apiClient.post<CreatePagoResponse>('/admin/pagos', dto);
  return data;
}

/**
 * Read-only advisory preview. Saving still recomputes FIFO inside the payment
 * transaction; this request never creates a payment or ledger movement.
 */
export async function previewPagoFifo(
  dto: PagoFifoPreviewRequest,
): Promise<PagoFifoPreviewResponse> {
  const { data } = await apiClient.post<PagoFifoPreviewResponse>('/admin/pagos/preview-fifo', dto);
  return data;
}

/**
 * Read-only prepaid preview. The backend resolves the current-period invoice
 * candidate and the first package period; the frontend never infers either
 * value from invoice ordering or ids.
 */
export async function previewPagoPrepago(
  dto: PagoPrepagoPreviewRequest,
): Promise<PagoPrepagoPreviewResponse> {
  const { data } = await apiClient.post<PagoPrepagoPreviewResponse>(
    '/admin/pagos/preview-prepago',
    dto,
  );
  return data;
}

/**
 * OCR de boleta (multipart). Devuelve CANDIDATOS (referencia/monto/fecha)
 * que el operador confirma/corrige en el form antes de crear el pago.
 * El backend guarda la imagen y devuelve su nombre en `archivo`.
 */
export async function uploadBoletaOcr(file: File): Promise<BoletaOcrResult> {
  const formData = new FormData();
  formData.append('archivo', file);
  // This per-request value prevents Axios 1.19 transformRequest from
  // JSON-serializing FormData because the shared client defaults to
  // `Content-Type: application/json`. The browser adapter's resolveConfig then
  // removes this header for the FormData body, allowing the browser to generate
  // the multipart boundary. Without the override, FileInterceptor receives no
  // file and the backend returns HTTP 400.
  const { data } = await apiClient.post<BoletaOcrResult>('/admin/pagos/boleta/ocr', formData, {
    timeout: 60_000,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function descargarBoletaPago(id: number): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(`/admin/pagos/${id}/boleta`, {
    responseType: 'blob',
  });
  return data;
}

export async function anularPago(id: number, dto: AnularPagoDto): Promise<PagoClienteResponse> {
  const { data } = await apiClient.post<PagoClienteResponse>(`/admin/pagos/${id}/anular`, dto);
  return data;
}

/**
 * Descarga el recibo del pago desde el servidor (GET /admin/pagos/:id/recibo).
 * Devuelve bytes archivados de la versión elegida (o la última). Sin versiones,
 * la primera descarga emite una versión inicial. Para emitir una actualización
 * explícita se usa emitirVersionRecibo; reimpresion se conserva por compatibilidad.
 */
export async function descargarReciboPago(
  id: number,
  reimpresion: boolean,
  version?: number,
): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(`/admin/pagos/${id}/recibo`, {
    params: version != null ? { version } : reimpresion ? { reimpresion: 'true' } : undefined,
    responseType: 'blob',
  });
  return data;
}
